import ema from "./ema";
import { isDefined, zipper } from "../utils";
import { Klinger as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { signalPeriods, shortCycle, longCycle } = options;

    if (!data || data.length < Math.max(shortCycle, longCycle) + 1) {
      return [];
    }

    // Step 1: Calculate signed volume based on price direction
    const signedVolumeArray = [];

    for (let i = 0; i < data.length; i++) {
      const bar = data[i];

      if (
        !bar ||
        !isDefined(bar.high) ||
        !isDefined(bar.low) ||
        !isDefined(bar.close)
      ) {
        signedVolumeArray.push(undefined);
        continue;
      }

      // Extract volume
      let vol = bar.volume;
      if (typeof vol === "object" && vol !== null) {
        vol = vol.volume;
      }

      if (!isDefined(vol) || vol <= 0) {
        signedVolumeArray.push(undefined);
        continue;
      }

      // Calculate typical price (hlc/3)
      const typicalPrice = (bar.high + bar.low + bar.close) / 3;

      // Determine signed volume based on price direction
      if (i === 0) {
        // First bar - no previous bar to compare
        signedVolumeArray.push(vol);
      } else {
        const prevBar = data[i - 1];
        if (
          prevBar &&
          isDefined(prevBar.high) &&
          isDefined(prevBar.low) &&
          isDefined(prevBar.close)
        ) {
          const prevTypicalPrice =
            (prevBar.high + prevBar.low + prevBar.close) / 3;

          // If price went up, volume is positive; if down, negative
          if (typicalPrice > prevTypicalPrice) {
            signedVolumeArray.push(vol);
          } else if (typicalPrice < prevTypicalPrice) {
            signedVolumeArray.push(-vol);
          } else {
            // No change - keep as positive
            signedVolumeArray.push(vol);
          }
        } else {
          signedVolumeArray.push(vol);
        }
      }
    }

    // Step 2: Calculate EMAs of signed volume
    const shortEMA = ema().options({
      windowSize: shortCycle,
      sourcePath: undefined,
    });

    const longEMA = ema().options({
      windowSize: longCycle,
      sourcePath: undefined,
    });

    const signalEMA = ema().options({
      windowSize: signalPeriods,
      sourcePath: undefined,
    });

    // Calculate short and long EMAs
    const shortEMAArray = shortEMA(signedVolumeArray);
    const longEMAArray = longEMA(signedVolumeArray);

    // Step 3: Calculate Klinger = Short EMA - Long EMA
    const klingerCalculator = zipper().combine((shortEMA, longEMA) =>
      isDefined(shortEMA) && isDefined(longEMA)
        ? shortEMA - longEMA
        : undefined,
    );

    const klingerArray = klingerCalculator(shortEMAArray, longEMAArray);

    // Step 4: Calculate Signal = EMA of Klinger
    const undefinedArray = new Array(longCycle);
    const signalArray = undefinedArray.concat(
      signalEMA(klingerArray.slice(longCycle)),
    );

    // Step 5: Calculate Divergence = Klinger - Signal
    const zip = zipper().combine((klinger, signal) => ({
      macd: klinger, // Using 'macd' key for compatibility with MACDSeries
      signal,
      divergence:
        isDefined(klinger) && isDefined(signal) ? klinger - signal : undefined,
    }));

    const klinger = zip(klingerArray, signalArray);

    return klinger;
  }

  calculator.undefinedLength = function () {
    const { longCycle, signalPeriods } = options;
    return longCycle + signalPeriods - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
